/**
 * useFormatPrice — resolves the storefront's currency and returns an
 * `Intl.NumberFormat`-backed `format(n)` helper.
 *
 * Resolution order:
 *   1. branch-level `currency` on `branchConfig.branches[0]`
 *   2. root-level `currency` on the `OnlineOrderingConfig` payload
 *   3. menu-level currency observed by `useMenu` (every catalog `PricingInfo`
 *      carries one — most accurate when the branch config doesn't expose it)
 *   4. fallback to "USD"
 *
 * Tenants whose `branchConfig` doesn't yet declare a currency (the SDK type
 * doesn't model the field today) still get the right symbol because the menu
 * is the source of truth for prices anyway.
 */
import { useMemo, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";

import { scopedStorageKey, storageScopeSlug } from "@/lib/storageScope";
import { useBranch } from "@/store/branch";

const MENU_CURRENCY_KEY = "v2_menu_currency";

// Tiny module-level store so `useMenu` can publish the currency it observed,
// keyed by storefront slug. Deep-link refreshes can then recover the last
// menu currency before the menu hook has loaded on that page.
const menuCurrencyByScope = new Map<string, string | null>();
const subs = new Set<() => void>();

function readStoredMenuCurrency(slug?: string | null): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(scopedStorageKey(MENU_CURRENCY_KEY, slug));
  } catch {
    return null;
  }
}

export function setMenuCurrency(
  c: string | null | undefined,
  slug?: string | null,
): void {
  const scope = storageScopeSlug(slug);
  const next = c ?? null;
  if (next === menuCurrencyByScope.get(scope)) return;
  menuCurrencyByScope.set(scope, next);
  if (typeof window !== "undefined") {
    try {
      const key = scopedStorageKey(MENU_CURRENCY_KEY, scope);
      if (next) localStorage.setItem(key, next);
      else localStorage.removeItem(key);
    } catch {
      /* storage unavailable */
    }
  }
  subs.forEach((fn) => fn());
}

function subscribe(fn: () => void): () => void {
  subs.add(fn);
  return () => {
    subs.delete(fn);
  };
}
function getSnapshot(slug?: string | null): string | null {
  const scope = storageScopeSlug(slug);
  if (menuCurrencyByScope.has(scope))
    return menuCurrencyByScope.get(scope) ?? null;
  const stored = readStoredMenuCurrency(scope);
  menuCurrencyByScope.set(scope, stored);
  return stored;
}

export function useFormatPrice() {
  const { branch: activeBranch, branchConfig, branchSlug } = useBranch();
  const { i18n } = useTranslation();
  const observedFromMenu = useSyncExternalStore(
    subscribe,
    () => getSnapshot(branchSlug),
    () => getSnapshot(branchSlug),
  );

  // Both fields are absent from the generated SDK types today, so cast to a
  // permissive shape and read defensively.
  const branch = activeBranch as
    | { currency?: string | null; currencyCode?: string | null }
    | undefined;
  const currency =
    branch?.currency ??
    branch?.currencyCode ??
    (
      branchConfig as
        | { currency?: string | null; currencyCode?: string | null }
        | null
        | undefined
    )?.currency ??
    (
      branchConfig as
        | { currency?: string | null; currencyCode?: string | null }
        | null
        | undefined
    )?.currencyCode ??
    observedFromMenu ??
    "USD";

  const fmt = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language || "en", {
        style: "currency",
        currency,
        currencyDisplay: "narrowSymbol",
      }),
    [i18n.language, currency],
  );

  const egpNumberFmt = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language || "en", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [i18n.language],
  );

  const normalizedCurrency = String(currency || "").toUpperCase();
  const isEgp = normalizedCurrency === "EGP" || normalizedCurrency === "ج.م";
  const isArabic = (i18n.language || "en").toLowerCase().startsWith("ar");

  function formatAmount(n: number | null | undefined): string {
    const amount = typeof n === "number" ? n : 0;
    if (isEgp) {
      const num = egpNumberFmt.format(amount);
      return isArabic ? `${num} جنيه` : `${num} EGP`;
    }
    return fmt.format(amount);
  }

  return {
    currency,
    format: formatAmount,
  };
}
